// ============================================================
//  PushBell.js — "Push notifications" button + panel
//  Used in the chat app header (/chats) and on Website Widget.
//  Turn it on once per device: new chats then arrive as phone/desktop
//  notifications with sound, even when CommandKit is closed.
// ============================================================
import './PushBell.css';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { pushStatus, enablePush, disablePush, testPush } from './livePush';

export default function PushBell({ tone = 'light' }) {
  const [open, setOpen]     = useState(false);
  const [st, setSt]         = useState(null);       // pushStatus()
  const [busy, setBusy]     = useState('');
  const [quick, setQuick]   = useState('');
  const wrapRef = useRef(null);

  const refresh = useCallback(async () => {
    try {
      const s = await pushStatus();
      setSt(s);
      setQuick(s.quick || '');
    } catch { setSt(prev => prev || { support: 'ok', permission: 'default', on: false, devices: [] }); }
  }, []);
  useEffect(() => { refresh(); }, [refresh]);
  useEffect(() => { if (open) refresh(); }, [open, refresh]);   // permission may have changed in the browser settings

  useEffect(() => {
    if (!open) return undefined;
    const away = e => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    const esc = e => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('pointerdown', away);
    document.addEventListener('keydown', esc);
    return () => { document.removeEventListener('pointerdown', away); document.removeEventListener('keydown', esc); };
  }, [open]);

  async function run(kind, fn, okMsg) {
    setBusy(kind);
    try { await fn(); if (okMsg) toast.success(okMsg); await refresh(); }
    catch (e) { toast.error(e?.response?.data?.message || e?.message || 'Something went wrong'); await refresh(); }
    setBusy('');
  }

  const on = !!st?.on;
  const quickChanged = on && quick.trim() !== (st?.quick || '');

  return (
    <div className={`pbell is-${tone}`} ref={wrapRef}>
      <button type="button" className={`pbell-btn ${on ? 'is-on' : ''}`} onClick={() => setOpen(o => !o)}
        aria-expanded={open} aria-haspopup="dialog" title={on ? 'Push notifications are on' : 'Get push notifications'}>
        <i className={`fa-${on ? 'solid' : 'regular'} fa-bell`} />
        <span className="pbell-label">{on ? 'Push on' : 'Push'}</span>
        <i className={`pbell-dot ${on ? 'is-on' : ''}`} aria-hidden="true" />
      </button>

      {open && (
        <div className="pbell-panel" role="dialog" aria-label="Push notifications">
          <div className="pbell-head">
            <span className="pbell-ic"><i className="fa-solid fa-bell" /></span>
            <div>
              <b>Push notifications</b>
              <span className={`pbell-pill ${on ? 'is-on' : ''}`}>{on ? 'On for this device' : 'Off on this device'}</span>
            </div>
          </div>
          <p className="pbell-lead">A notification with sound for every new chat — <b>even when CommandKit is closed</b>. Reply right from the notification.</p>

          {!st ? (
            <div className="pbell-note"><span className="wwg-spin" /> Checking this device…</div>
          ) : st.support === 'ios-install' ? (
            <div className="pbell-note is-warn">
              <i className="fa-solid fa-mobile-screen" />
              <span>On iPhone/iPad, open <b>CommandKit Chats</b> from your Home Screen first (Share <i className="fa-solid fa-arrow-up-from-bracket" /> → Add to Home Screen), then turn push on in the app.</span>
            </div>
          ) : st.support === 'unsupported' ? (
            <div className="pbell-note is-warn"><i className="fa-solid fa-circle-info" /><span>This browser can’t receive push notifications. Try Chrome, Edge, Firefox or Safari.</span></div>
          ) : st.permission === 'denied' ? (
            <div className="pbell-note is-bad">
              <i className="fa-solid fa-bell-slash" />
              <span>Notifications are blocked for this site. Click the <b>lock icon</b> next to the address, allow <b>Notifications</b>, then reload.</span>
            </div>
          ) : !on ? (
            <button type="button" className="pbell-main" onClick={() => run('on', () => enablePush(quick.trim()), 'Push notifications are on 🎉')} disabled={!!busy}>
              {busy === 'on' ? <span className="wwg-spin" /> : <i className="fa-solid fa-bolt" />} Turn on push notifications
            </button>
          ) : (
            <>
              <label className="pbell-field">
                <span>Quick reply <em>— one tap on the notification sends this</em></span>
                <textarea rows={2} maxLength={200} value={quick} placeholder={st.quickDefault} onChange={e => setQuick(e.target.value)} />
              </label>
              {quickChanged && (
                <button type="button" className="pbell-save" onClick={() => run('save', () => enablePush(quick.trim()), 'Quick reply saved')} disabled={!!busy}>
                  {busy === 'save' ? <span className="wwg-spin" /> : <i className="fa-solid fa-check" />} Save quick reply
                </button>
              )}
              <div className="pbell-row">
                <button type="button" onClick={() => run('test', testPush, 'Test sent — check your notifications')} disabled={!!busy}>
                  {busy === 'test' ? <span className="wwg-spin" /> : <i className="fa-regular fa-paper-plane" />} Send a test
                </button>
                <button type="button" className="is-off" onClick={() => run('off', disablePush, 'Push notifications are off for this device')} disabled={!!busy}>
                  {busy === 'off' ? <span className="wwg-spin" /> : <i className="fa-regular fa-bell-slash" />} Turn off
                </button>
              </div>
            </>
          )}

          {st?.devices?.length > 0 && (
            <div className="pbell-devices">
              <span>{st.devices.length === 1 ? '1 device gets notifications' : `${st.devices.length} devices get notifications`}</span>
              <ul>{st.devices.slice(0, 4).map(d => <li key={d.id}><i className="fa-solid fa-display" /> {d.label || 'Device'}</li>)}</ul>
            </div>
          )}
          <p className="pbell-tip">Notifications use your device’s own sound. If you hear nothing, check that Do Not Disturb / Focus is off.</p>
        </div>
      )}
    </div>
  );
}
